import React from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import type { ModuleRouteDefinition } from '@/types/route'

interface SidebarSectionProps {
  label: string
  routes: ModuleRouteDefinition[]
}

export function SidebarSection({ label, routes }: SidebarSectionProps) {
  return (
    <div className="mb-4">
      <p className="text-tiny text-text-dim uppercase tracking-wider px-3 mb-1">{label}</p>
      <ul className="flex flex-col gap-0.5">
        {routes.map((route) => (
          <li key={route.id}>
            <NavLink
              to={route.path}
              end={route.path.split('/').filter(Boolean).length === 2}
              className={({ isActive }) =>
                `block px-3 py-1.5 rounded text-body transition-colors ${
                  isActive
                    ? 'text-accent bg-accent-bg border-l-2 border-accent pl-2.5'
                    : 'text-text-body hover:text-text hover:bg-surface-hover'
                }`
              }
            >
              {route.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </div>
  )
}

interface ModuleSidebarProps {
  routes: ModuleRouteDefinition[]
}

export function ModuleSidebar({ routes }: ModuleSidebarProps) {
  const _location = useLocation()

  // Group routes by navGroup
  const groups: Record<string, ModuleRouteDefinition[]> = {}
  for (const route of routes) {
    if (!groups[route.navGroup]) groups[route.navGroup] = []
    groups[route.navGroup].push(route)
  }

  const groupLabels: Record<string, string> = {
    overview: 'Overview',
    analysis: 'Analysis',
    content: 'Content',
    operations: 'Operations',
  }

  return (
    <aside
      className="fixed left-0 bottom-0 z-40 bg-bg border-r border-border overflow-y-auto hidden lg:block"
      style={{ top: 48, width: 200, paddingTop: 24 }}
    >
      <nav className="px-2">
        {Object.entries(groups)
          .sort(([a], [b]) => {
            const order = ['overview', 'analysis', 'content', 'operations']
            return order.indexOf(a) - order.indexOf(b)
          })
          .map(([group, groupRoutes]) => (
            <SidebarSection
              key={group}
              label={groupLabels[group] ?? group}
              routes={groupRoutes.sort((a, b) => a.sidebarOrder - b.sidebarOrder)}
            />
          ))}
      </nav>
    </aside>
  )
}
